/**
 * ArrivalHome.jsx — Phase 5.1-A / 5.1-B / visual completion
 *
 * The front door to a relationship.
 *
 * Implements the Founder-authorized Phase 5.1 component redline
 * (ARVEXIS_PHASE_5_1_COMPONENT_REDLINE_SPEC.pdf) over the accepted written contract.
 *
 * Mobile hierarchy (fixed): wordmark + menu (App.js header) → orb → greeting → supporting line
 *   → Message | Talk → Dream | Dream Journal → optional generic Continue Conversation.
 * Desktop ≥1200px: genuine two-zone composition — orb in a left companion field, the same
 *   hierarchy as a right action field. Never an enlarged mobile column.
 *
 * Contract held here:
 * - Message and Talk carry equal primary weight and are rendered IMMEDIATELY. No entrance
 *   animation, and they never wait on the atmospheric plate decoding.
 * - The resting orb is operationally neutral: no listening, speaking or processing signal.
 *   ArvexisPresence, its state logic and /orb-galaxy.png are untouched — display size and
 *   position only.
 * - Continuity is optional, last, generically worded, offered only when an EXACT eligible
 *   Message session can be revalidated, and it opens that exact session. No topic, title,
 *   excerpt, category, timestamp or subject matter. Nothing simulated.
 * - New member: no continuity affordance and "begin" language. Returning member: "continue".
 *   No language implying Arvexis waited, watched, worried or thought off-screen.
 * - Home stores nothing, caches nothing, records nothing and calls no provider.
 * - No bottom navigation and no Profile destination (Phase 6).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Moon, BookOpen, AudioLines, MessageCircle, ChevronRight } from 'lucide-react';
import ArvexisPresence from './ArvexisPresence';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Redline colour system
const C = {
  canvas: '#030A12',
  ivory: '#F4EFE7',
  silver: '#A8B3BF',
  champagne: '#E8C58F',
  focus: '#F5D9AC'
};

const SERIF = "'Cormorant Garamond', Georgia, 'Times New Roman', serif";
const SANS = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";

// ============================================================================
// TIME-AWARE GREETING — the device clock only. No claim about Arvexis.
// ============================================================================
const getTimeGreeting = (lang = 'en') => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return lang === 'es' ? 'Buenos días' : 'Good morning';
  if (hour >= 12 && hour < 17) return lang === 'es' ? 'Buenas tardes' : 'Good afternoon';
  return lang === 'es' ? 'Buenas noches' : 'Good evening';
};

// ============================================================================
// ATMOSPHERE — decorative raster plate over a lightweight CSS midnight fallback.
// Exactly one plate is requested per viewport; the unmatched <source> is never fetched.
// Absent from the accessibility tree. Never animated, panned or parallaxed.
// ============================================================================
const HomeAtmosphere = () => (
  <div
    className="absolute inset-0 overflow-hidden pointer-events-none select-none"
    aria-hidden="true"
    data-testid="home-atmosphere"
    style={{
      background:
        'radial-gradient(120% 70% at 50% 22%, #0A1826 0%, #061120 45%, #030A12 100%)'
    }}
  >
    <picture>
      <source
        media="(min-width: 768px)"
        type="image/webp"
        srcSet="/backgrounds/home-atmosphere-desktop.webp"
      />
      <source media="(min-width: 768px)" srcSet="/backgrounds/home-atmosphere-desktop.jpg" />
      <source type="image/webp" srcSet="/backgrounds/home-atmosphere-mobile.webp" />
      <img
        src="/backgrounds/home-atmosphere-mobile.jpg"
        alt=""
        aria-hidden="true"
        decoding="async"
        fetchPriority="low"
        draggable="false"
        data-testid="home-atmosphere-plate"
        className="absolute inset-0 w-full h-full object-cover"
      />
    </picture>
    {/* Restrained scrim — protects functional text over the plate's brightest region. */}
    <div
      className="absolute inset-0"
      style={{
        background:
          'linear-gradient(180deg, rgba(3,10,18,0.34) 0%, rgba(3,10,18,0.52) 52%, rgba(3,10,18,0.80) 100%)'
      }}
    />
  </div>
);

// ============================================================================
// ACTION CONTROLS
// Rest / hover / pressed / focus / disabled per redline. State is never carried by
// glow or colour alone: every state also changes border weight or opacity.
// ============================================================================
const FOCUS =
  'outline-none focus-visible:ring-2 focus-visible:ring-[#F5D9AC]/75 focus-visible:ring-offset-[3px] focus-visible:ring-offset-transparent';

const PrimaryAction = ({ icon: Icon, label, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`group relative flex-1 min-w-0 flex items-center rounded-full
      pl-[10px] pr-[12px] min-[1200px]:pl-6 min-[1200px]:pr-6
      h-[62px] min-[768px]:h-[68px] min-[1200px]:h-[76px]
      border border-[#E8C58F]/[0.56] bg-[#E8C58F]/[0.06]
      shadow-[0_0_12px_rgba(232,197,143,0.14)]
      transition-[background-color,border-color,box-shadow] duration-200
      hover:border-[#E8C58F]/70 hover:bg-[#E8C58F]/[0.09]
      active:border-[#E8C58F]/[0.82] active:bg-[#E8C58F]/[0.13] active:shadow-none
      ${FOCUS}`}
  >
    <span className="flex-1 min-w-0 flex items-center justify-center gap-1.5 min-[1200px]:gap-2.5">
      <Icon
        className="w-[20px] h-[20px] min-[1200px]:w-6 min-[1200px]:h-6 shrink-0 text-[#E8C58F] group-hover:text-[#F5D9AC] transition-colors"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      />
      <span
        data-testid={`${testId}-label`}
        className="text-[17px] min-[768px]:text-[19px] min-[1200px]:text-[21px] font-semibold min-[1200px]:font-medium leading-none whitespace-nowrap"
        style={{ fontFamily: SANS, color: C.ivory }}
      >
        {label}
      </span>
    </span>
    <ChevronRight
      className="shrink-0 w-[16px] h-[16px] min-[1200px]:w-[18px] min-[1200px]:h-[18px] text-[#E8C58F]/70 group-hover:text-[#F5D9AC] transition-colors"
      strokeWidth={1.5}
      aria-hidden="true"
    />
  </button>
);

const JourneyAction = ({ icon: Icon, label, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`group relative flex-1 min-w-0 flex items-center rounded-full pl-[6px] pr-[12px] min-[1200px]:pl-6 min-[1200px]:pr-6
      h-[56px] min-[768px]:h-[60px] min-[1200px]:h-[66px]
      border border-[#8095A6]/[0.65] bg-[#071421]/[0.74]
      transition-[background-color,border-color] duration-200
      hover:border-[#8095A6]/[0.82] hover:bg-[#071421]/[0.9]
      active:border-[#8095A6]/95 active:bg-[#071421]/[0.96]
      ${FOCUS}`}
  >
    <span className="flex-1 min-w-0 flex items-center justify-center gap-1 min-[1200px]:gap-2.5">
      <Icon
        className="w-[22px] h-[22px] min-[1200px]:w-6 min-[1200px]:h-6 shrink-0 text-[#A8B3BF] group-hover:text-[#F4EFE7] transition-colors"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      />
      <span
        data-testid={`${testId}-label`}
        className="text-[14px] min-[768px]:text-[17px] min-[1200px]:text-[18px] leading-[1.15] text-center"
        style={{ fontFamily: SANS, color: C.silver }}
      >
        {label}
      </span>
    </span>
    <ChevronRight
      className="shrink-0 w-[18px] h-[18px] text-[#8095A6] group-hover:text-[#A8B3BF] transition-colors"
      strokeWidth={1.5}
      aria-hidden="true"
    />
  </button>
);

// ============================================================================
// ARRIVAL HOME
// ============================================================================
const ArrivalHome = ({ lang = 'en', user }) => {
  const navigate = useNavigate();
  // The exact eligible Message session, or nothing. Id only — never a topic, never an excerpt.
  const [resumable, setResumable] = useState(null);
  // Whether this member has ever held a Message conversation. Governs begin vs continue wording
  // only; it never becomes a claim about Arvexis or a description of the past.
  const [hasHistory, setHasHistory] = useState(false);
  const [resumeNotice, setResumeNotice] = useState(null);
  const [opening, setOpening] = useState(false);
  const mountedRef = useRef(true);

  const firstName = user?.name?.split(' ')[0] || '';
  const es = lang === 'es';

  // StrictMode mounts, unmounts and remounts in development, so the flag must be re-armed on
  // every mount — otherwise the resume handler would silently refuse to act after a remount.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // 5.1-B. Revalidation: a session may only be offered if it still exists and still belongs to
  // this member. The member-scoped endpoint answers both questions, and its 404 is the
  // authorization gate.
  const revalidate = useCallback(async (sessionId) => {
    try {
      const { data } = await axios.get(`${API}/companion/sessions/${sessionId}`);
      return data?.session_id === sessionId;
    } catch (error) {
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/companion/sessions`);
        const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
        const withTurns = sessions.filter((s) => s?.session_id && (s?.message_count || 0) > 0);
        if (!cancelled) setHasHistory(withTurns.length > 0);
        const candidate = withTurns
          .sort((a, b) => new Date(b.last_activity || b.started_at) - new Date(a.last_activity || a.started_at))[0];
        if (!candidate) return;
        const ok = await revalidate(candidate.session_id);
        if (!cancelled && ok) setResumable({ sessionId: candidate.session_id });
      } catch (error) {
        // No continuity signal is better than a guessed one.
        if (!cancelled) { setResumable(null); setHasHistory(false); }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [revalidate]);

  // Two distinct, truthful destinations. New Message carries a request for a new session and no
  // id whatsoever; continuity carries one exact revalidated id. Neither can inherit the other's.
  const openNewMessage = () => {
    navigate('/companion', { state: { chat: true, newSession: true } });
  };

  const openExistingMessage = (sessionId) => {
    navigate('/companion', { state: { chat: true, sessionId } });
  };

  const openTalk = () => navigate('/companion', { state: { voice: true } });

  // 5.1-B. Revalidated again immediately before opening: between arriving at Home and tapping,
  // the conversation may have been deleted somewhere else. Nothing is opened on a stale promise.
  const handleResume = async () => {
    if (!resumable || opening) return;
    setOpening(true);
    const ok = await revalidate(resumable.sessionId);
    if (!mountedRef.current) return;
    if (!ok) {
      setResumable(null);
      setResumeNotice(es
        ? 'Esa conversación ya no está disponible. Puedes empezar una nueva.'
        : 'That conversation is no longer available. You can start a new one.');
      setOpening(false);
      return;
    }
    openExistingMessage(resumable.sessionId);
  };

  const greeting = getTimeGreeting(lang);
  const subline = hasHistory
    ? (es ? '¿Cómo quieres continuar?' : 'How would you like to continue?')
    : (es ? '¿Cómo quieres comenzar?' : 'How would you like to begin?');

  // ---------------------------------------------------------------------------
  // Composed pieces — identical hierarchy, two compositions.
  // ---------------------------------------------------------------------------
  const Orb = (
    <button
      type="button"
      onClick={openTalk}
      className={`relative rounded-full ${FOCUS}`}
      data-testid="orb-touch-target"
      aria-label={es ? 'Hablar con Arvexis' : 'Talk with Arvexis'}
    >
      <ArvexisPresence
        size="large"
        state="idle"
        className="!w-[230px] !h-[230px] min-[768px]:!w-[320px] min-[768px]:!h-[320px] min-[1200px]:!w-[380px] min-[1200px]:!h-[380px] min-[1600px]:!w-[520px] min-[1600px]:!h-[520px]"
      />
    </button>
  );

  const Recognition = (
    <div className="text-center min-[1200px]:!text-left">
      <h1
        className="text-[30px] leading-[36px] min-[768px]:text-[40px] min-[768px]:leading-[46px] min-[1200px]:text-[52px] min-[1200px]:leading-[60px]"
        style={{ fontFamily: SERIF, fontWeight: 600, color: C.ivory }}
        data-testid="home-greeting"
      >
        {greeting}{firstName ? `, ${firstName}` : ''}.
      </h1>
      <p
        className="mt-2 text-[17px] leading-[24px] min-[768px]:text-[19px] min-[768px]:leading-[27px] min-[1200px]:text-[22px] min-[1200px]:leading-[30px]"
        style={{ fontFamily: SANS, color: C.silver }}
        data-testid="home-subline"
      >
        {subline}
      </p>
    </div>
  );

  const Actions = (
    <div className="w-full">
      <div className="flex items-stretch gap-[14px] min-[1200px]:gap-4" data-testid="home-primary-actions">
        <PrimaryAction
          icon={MessageCircle}
          label={es ? 'Mensaje' : 'Message'}
          onClick={openNewMessage}
          testId="home-message-btn"
        />
        <PrimaryAction
          icon={AudioLines}
          label={es ? 'Hablar' : 'Talk'}
          onClick={openTalk}
          testId="home-talk-btn"
        />
      </div>

      <div
        className="mt-[14px] min-[1200px]:mt-4 flex items-stretch gap-[14px] min-[1200px]:gap-4"
        data-testid="home-journey-actions"
      >
        <JourneyAction
          icon={Moon}
          label={es ? 'Registra un sueño' : 'Record Dream'}
          onClick={() => navigate('/log')}
          testId="home-dream-btn"
        />
        <JourneyAction
          icon={BookOpen}
          label={es ? 'Diario de Sueños' : 'Dream Journal'}
          onClick={() => navigate('/dreams')}
          testId="home-dream-journal-btn"
        />
      </div>

      {/* CONTINUITY — last, optional, generic. No topic. No excerpt. No timestamp. */}
      {resumable && (
        <button
          type="button"
          onClick={handleResume}
          disabled={opening}
          data-testid="home-continue-in-message"
          className={`group relative w-full mt-[18px] flex items-center pl-[20px] pr-[18px]
            h-[72px] min-[1200px]:h-[82px] rounded-[24px]
            border border-[#8095A6]/[0.65] bg-[#071421]/[0.74]
            transition-[background-color,border-color] duration-200
            hover:border-[#8095A6]/[0.82] hover:bg-[#071421]/[0.9]
            active:border-[#8095A6]/95 active:bg-[#071421]/[0.96]
            disabled:opacity-[0.42] disabled:shadow-none
            ${FOCUS}`}
        >
          <MessageCircle
            className="shrink-0 w-[22px] h-[22px] min-[1200px]:w-6 min-[1200px]:h-6 text-[#8095A6] group-hover:text-[#A8B3BF] transition-colors"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          />
          <span
            data-testid="home-continue-in-message-label"
            className="flex-1 min-w-0 text-center text-[15px] min-[768px]:text-[17px] min-[1200px]:text-[18px] font-medium leading-tight"
            style={{ fontFamily: SANS, color: C.silver }}
          >
            {es ? 'Continuar conversación' : 'Continue Conversation'}
          </span>
          <ChevronRight
            className="shrink-0 w-[18px] h-[18px] text-[#8095A6] group-hover:text-[#A8B3BF] transition-colors"
            strokeWidth={1.5}
            aria-hidden="true"
          />
        </button>
      )}

      {resumeNotice && (
        <p
          className="mt-4 text-[15px] leading-[22px] text-center min-[1200px]:!text-left"
          style={{ fontFamily: SANS, color: C.silver }}
          role="status"
          data-testid="home-continue-unavailable"
        >
          {resumeNotice}
        </p>
      )}
    </div>
  );

  return (
    <div
      className="fixed inset-0 overflow-hidden"
      style={{ background: C.canvas, height: '100dvh' }}
      data-testid="arrival-home"
    >
      <HomeAtmosphere />

      <main
        className="relative z-10 h-full overflow-y-auto flex flex-col items-center justify-center
          pl-[max(24px,env(safe-area-inset-left,0px))] pr-[max(24px,env(safe-area-inset-right,0px))]
          min-[1200px]:pl-16 min-[1200px]:pr-16"
        style={{
          paddingTop: 'calc(max(20px, env(safe-area-inset-top, 0px) + 8px) + 56px)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 15px)'
        }}
      >
        {/* One tree, two compositions. ≤1199 px: a single centred column, orb → greeting →
            actions. ≥1200 px: a genuine two-zone field — orb left, the same hierarchy right.
            Nothing is duplicated, so every control exists exactly once. */}
        <div
          className="w-full max-w-[342px] min-[768px]:max-w-[640px] min-[1200px]:max-w-[1480px]
            flex flex-col items-center
            min-[1200px]:!flex-row min-[1200px]:justify-center min-[1200px]:gap-[120px]"
          data-testid="home-composition"
        >
          <div className="min-[1200px]:flex-1 min-[1200px]:flex min-[1200px]:justify-end min-[1200px]:min-w-0">
            <div className="min-[1200px]:mb-[10vh]">{Orb}</div>
          </div>
          <div
            className="w-full mt-6 min-[1200px]:!mt-0
              min-[1200px]:w-[560px] min-[1600px]:w-[720px] min-[1200px]:shrink-0
              flex flex-col items-center min-[1200px]:!items-start"
            data-testid="home-action-field"
          >
            {Recognition}
            <div className="mt-7 min-[1200px]:!mt-10 w-full">{Actions}</div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ArrivalHome;
